import { useMemo, useState } from "react"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  useTheme
} from "@mui/material"
import { useNavigate } from "react-router-dom"

import { ChipStyled, Stack } from "src/shared/components"
import EmptyBox from "src/shared/components/empty"
import { usePermission } from "src/shared/hooks/usePermission"
import ROUTE_PATHS from "src/routes/route-path"
import { useIntegrationCatalogueQuery } from "src/graphql/quark/quark.type"
import ConnectIntegrationModal from "../../components/ConnectIntegrationModal"
import ConnectorBadge from "../../components/ConnectorBadge"

type CatalogueEntry = {
  connectorCode: string
  displayName: string
  description: string
  category: string
  entitled: boolean
  connectionId?: string | null
  settings: {
    key: string
    label: string
    helpText?: string | null
    secret: boolean
    required: boolean
  }[]
}

type EntryState = "connected" | "available" | "locked"

const stateOf = (entry: CatalogueEntry): EntryState => {
  if (entry.connectionId) return "connected"
  return entry.entitled ? "available" : "locked"
}

/** Connected first — those are the ones an operator comes here to manage. */
const SORT_ORDER: Record<EntryState, number> = {
  connected: 0,
  available: 1,
  locked: 2
}

const STATUS_LABEL: Record<EntryState, string> = {
  connected: "Connected",
  available: "Available",
  locked: "Not enabled"
}

const STATUS_TONE: Record<EntryState, string> = {
  connected: "success",
  available: "info",
  locked: "warning"
}

const IntegrationsCatalogue = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { hasPermission } = usePermission()
  const canConnect = hasPermission("system:integrations:create")

  const [connecting, setConnecting] = useState<CatalogueEntry | null>(null)

  const { data, loading } = useIntegrationCatalogueQuery({
    fetchPolicy: "network-only"
  })

  const entries: CatalogueEntry[] = useMemo(
    () =>
      [...(data?.fusionIntegrationCatalogue ?? [])].sort(
        (a, b) =>
          SORT_ORDER[stateOf(a)] - SORT_ORDER[stateOf(b)] ||
          a.displayName.localeCompare(b.displayName)
      ),
    [data]
  )

  const connectedCount = entries.filter((e) => stateOf(e) === "connected").length

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    )

  return (
    <Box p={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 1.5 }}
        sx={{ mb: 3 }}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="h3">Integrations</Typography>
          <Typography variant="body2" color="text.secondary">
            Connect ONE to the services you already use.
          </Typography>
        </Stack>

        {entries.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
              {connectedCount}
            </Box>{" "}
            of {entries.length} connected
          </Typography>
        )}
      </Stack>

      {entries.length === 0 ? (
        <Box py={6}>
          <EmptyBox text="No integrations are available for your account yet." />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {entries.map((entry) => {
            const state = stateOf(entry)
            const locked = state === "locked"

            return (
              <Grid item xs={12} sm={6} lg={4} key={entry.connectorCode}>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "16px",
                    bgcolor: locked ? "transparent" : "background.paper",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                    ...(!locked && {
                      "&:hover": {
                        borderColor: alpha(theme.palette.primary.main, 0.28),
                        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`
                      }
                    })
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <ConnectorBadge
                      connectorCode={entry.connectorCode}
                      displayName={entry.displayName}
                      muted={locked}
                    />

                    <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{ letterSpacing: "-0.2px" }}
                        noWrap
                      >
                        {entry.displayName}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary" noWrap>
                        {entry.category}
                      </Typography>
                    </Stack>

                    <ChipStyled
                      size="small"
                      label={STATUS_LABEL[state]}
                      background={STATUS_TONE[state]}
                    />
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexGrow: 1, mt: 1.75 }}
                  >
                    {entry.description}
                  </Typography>

                  <Divider sx={{ my: 1.75 }} />

                  {locked ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                      <Typography variant="body2" color="text.disabled">
                        Contact support to enable this for your account.
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" justifyContent="flex-end">
                      {entry.connectionId ? (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                          onClick={() =>
                            navigate(
                              `${ROUTE_PATHS.INTEGRATIONS_HUB.BASE}/${entry.connectionId}`
                            )
                          }
                          sx={{ textTransform: "none" }}
                        >
                          Manage
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canConnect}
                          onClick={() => setConnecting(entry)}
                          sx={{ textTransform: "none" }}
                        >
                          Connect
                        </Button>
                      )}
                    </Stack>
                  )}
                </Box>
              </Grid>
            )
          })}
        </Grid>
      )}

      <ConnectIntegrationModal
        entry={connecting}
        onClose={() => setConnecting(null)}
        onConnected={(connectionId) =>
          navigate(`${ROUTE_PATHS.INTEGRATIONS_HUB.BASE}/${connectionId}`)
        }
      />
    </Box>
  )
}

export default IntegrationsCatalogue
